import { provideHttpClient } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';

import * as Sentry from '@sentry/angular';
import moment from 'moment-timezone';

import { provide } from '@core/common-legacy';
import {
  I18N_CONFIG_TOKEN,
  I18nService,
  LANGUAGE_CONFIG,
  LOCALE,
  patchMoment,
  PHRASE_APP_TRANSLATIONS,
  PhraseAppEditorService,
  PhraseAppTranslations,
  PhraseAppTranslationService,
  phraseAppTranslationsLoader,
  SupportedTranslations,
  TranslationsProvider,
} from '@core/i18n-legacy';
import {
  IconsContainerBundledStrategy,
  provideIconsContainerStrategy,
} from '@core/user-interface-legacy';

import { productUpdatesTranslations } from '../locales';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';
import { provideProductUpdatesConfig } from './app/config/product-updates-config.provider';
import { ProductUpdatesStateService } from './app/services/product-updates-state.service';
import { environment } from './environments/environment';

import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/fi';
import 'moment/locale/sv';

patchMoment(moment);
if (environment.angularProdMode) {
  enableProdMode();
}

const detectInitialLocale = () => {
  const { availableLocales } = environment.i18n;
  const urlLang = new URL(window.location.href).searchParams.get('lang');
  if (availableLocales.includes(urlLang!)) return urlLang!;

  const browserLang = navigator.language?.split('-')[0];
  if (availableLocales.includes(browserLang!)) return browserLang!;

  return environment.i18n.defaultLocale;
};

Sentry.init({ dsn: environment.sentry.dsn, environment: environment.sentry.environmentName });

const getI18nProviders = (lang: string, translations: PhraseAppTranslations[]) => [
  PhraseAppTranslationService,
  PhraseAppEditorService,
  provide(TranslationsProvider, { useExisting: PhraseAppTranslationService }),
  provideIconsContainerStrategy({ useClass: IconsContainerBundledStrategy }),
  provide(LANGUAGE_CONFIG, {
    useValue: {
      defaultLanguage: <SupportedTranslations>environment.i18n.defaultLocale,
      languages: environment.i18n.availableLocales,
    },
  }),
  provide(I18N_CONFIG_TOKEN, {
    useValue: {
      phraseAppProjectId: '',
      editorEnabled: false,
    },
  }),
  provide(LOCALE, { useValue: lang }),
  provide(PHRASE_APP_TRANSLATIONS, { useValue: translations }),
  I18nService,
];

const lang = detectInitialLocale();
moment.locale(lang);
phraseAppTranslationsLoader(
  <SupportedTranslations>lang,
  <SupportedTranslations>environment.i18n.defaultLocale,
  [<any>productUpdatesTranslations],
)
  .then(translations =>
    bootstrapApplication(AppComponent, {
      providers: [
        provideZonelessChangeDetection(),
        provideHttpClient(),
        provideRouter(
          APP_ROUTES,
          withComponentInputBinding(),
          withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
        ),
        provideProductUpdatesConfig(environment),
        provide(ErrorHandler, {
          useValue: Sentry.createErrorHandler(),
        }),
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        provideAppInitializer(() => {
          inject(Sentry.TraceService);
        }),
        ...getI18nProviders(lang, translations),
      ],
    }),
  )
  .then(appRef => {
    const stateService = appRef.injector.get(ProductUpdatesStateService);
    if (stateService.currentLocale() !== lang) {
      stateService.setLocale(lang, false);
    }
  })
  .catch(err => console.error(err));
